import { useCallback, useEffect, useState } from "react";

import { Agency, Carrier } from "../types";
import { fetchAgencies } from "../api";
import { PROVINCIAS } from "../provincias";

type AgencyPickerProps = {
  value: string;
  onChange: (agencyId: string) => void;
  // El CP de la cotización. Con él, la lista se limita a las sucursales de
  // ese código postal: el precio se cotizó para ese destino y el servidor
  // rechaza la orden si la sucursal está en otro lado.
  postalCode?: string;
  carrier?: Carrier;
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: 14,
};

// Selector de sucursal para la entrega "S": la lista sale del backend, que
// consulta las sucursales habilitadas del acuerdo (o el simulador).
export function AgencyPicker({ value, onChange, postalCode, carrier = "correo" }: AgencyPickerProps) {
  const [stateId, setStateId] = useState("");
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Con el CP de la cotización, el filtro por provincia sobra: un código
  // postal está en una sola provincia.
  const filtraPorCp = Boolean(postalCode);

  const cargar = useCallback(
    async (provincia: string, cp?: string) => {
      setLoading(true);
      setError("");

      try {
        setAgencies(await fetchAgencies(cp ? undefined : provincia || undefined, cp, carrier));
      } catch (err) {
        setAgencies([]);
        setError(err instanceof Error ? err.message : "No se pudieron consultar las sucursales.");
      } finally {
        setLoading(false);
      }
    },
    [carrier]
  );

  useEffect(() => {
    cargar(stateId, postalCode);
  }, [cargar, stateId, postalCode]);

  const elegida = agencies.find((agency) => agency.agencyId === value);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {filtraPorCp ? (
        <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 12 }}>
          Sucursales del código postal <strong>{postalCode}</strong>, que es el que se
          cotizó. Para retirar en otra localidad hay que cotizar a ese código postal.
        </p>
      ) : (
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>
            Provincia
          </span>
          <select
            value={stateId}
            onChange={(event) => {
              setStateId(event.target.value);
              onChange("");
            }}
            style={selectStyle}
          >
            <option value="">Todas las provincias</option>
            {PROVINCIAS.map((provincia) => (
              <option key={provincia.codigo} value={provincia.codigo}>
                {provincia.nombre}
              </option>
            ))}
          </select>
        </label>
      )}

      <label style={{ display: "grid", gap: 6 }}>
        <span style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>
          Sucursal de retiro
        </span>
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={loading || agencies.length === 0}
          style={selectStyle}
        >
          <option value="">
            {loading
              ? "Buscando sucursales..."
              : agencies.length === 0
                ? filtraPorCp
                  ? `Correo no tiene sucursales en el CP ${postalCode}`
                  : "Sin sucursales para ese filtro"
                : "Elegí una sucursal"}
          </option>
          {agencies.map((agency) => (
            <option key={agency.agencyId} value={agency.agencyId}>
              {agency.name}
              {agency.city ? ` · ${agency.city}` : ""}
              {agency.state ? ` (${agency.state})` : ""}
            </option>
          ))}
        </select>
      </label>

      {error && (
        <div style={{ color: "var(--danger)", fontSize: 13, fontWeight: 600 }}>{error}</div>
      )}

      {elegida && (
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: "10px 12px",
            background: "var(--surface-alt)",
            color: "var(--text-secondary)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div style={{ color: "var(--text-primary)", fontWeight: 700 }}>
            {elegida.name} ({elegida.agencyId})
          </div>
          {elegida.address && <div>{elegida.address}{elegida.zipCode ? ` · CP ${elegida.zipCode}` : ""}</div>}
          {elegida.schedule && <div>{elegida.schedule}</div>}
        </div>
      )}
    </div>
  );
}
